import React, { useMemo, useRef, useLayoutEffect, useState } from "react";

/**
 * Modern Sydney Trains platform indicator display.
 * White background, big orange T logo + destination, vertical scrolling
 * stops list, huge orange platform number and departs time,
 * with a "Next Service" mini-block at the bottom.
 *
 * Only the stops list scrolls; the header, badges, times and next-service
 * section are all static.
 */
export default function PlatformIndicatorDisplay({
  destination,
  platform,
  stops,
  serviceLabel,           // "Service" banner text
  stopsLabel,             // "All Stops" pill
  carriages,              // "8 carriages" pill
  departsRelative,        // "14 min"
  nextDestination,
  nextPlatform,
  nextDeparts,
  nextVia,
}) {
  const trimmedStops = (stops || []).filter(Boolean);
  const listRef = useRef(null);
  const viewportRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [duration, setDuration] = useState(30);

  // Only scroll when the list actually overflows the viewport.
  // Duration scales with content length so short lists don't blur past.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const list = listRef.current;
    if (!viewport || !list) return;
    // If we're already scrolling, the list contains 2 copies — halve it.
    const measured = list.scrollHeight;
    const singleCopy = shouldScroll ? measured / 2 : measured;
    const overflow = singleCopy > viewport.clientHeight + 4;
    if (overflow !== shouldScroll) setShouldScroll(overflow);
    // ~0.35s per station gives a comfortable read pace.
    const secs = Math.max(14, Math.min(90, trimmedStops.length * 2.2));
    setDuration(secs);
  }, [trimmedStops, shouldScroll]);

  const doubled = useMemo(
    () => (shouldScroll ? [...trimmedStops, ...trimmedStops] : trimmedStops),
    [trimmedStops, shouldScroll]
  );

  return (
    <div
      data-testid="platform-indicator-display"
      className="relative w-full h-full overflow-hidden select-none"
      style={{
        backgroundColor: "#FFFFFF",
        color: "#1B2C4E",
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
      }}
    >
      {/* Orange banner */}
      <div
        className="flex items-center px-[clamp(10px,1.8vw,28px)]"
        style={{
          backgroundColor: "#F58220",
          height: "clamp(24px,4vw,54px)",
        }}
        data-testid="platform-banner"
      >
        <span
          className="font-semibold text-white"
          style={{ fontSize: "clamp(11px,1.8vw,26px)", letterSpacing: "0.01em" }}
        >
          {serviceLabel || "Service"}
        </span>
      </div>

      <div
        className="grid gap-[clamp(6px,1vw,16px)] px-[clamp(10px,1.8vw,28px)] pt-[clamp(6px,1vw,16px)] h-[calc(100%-clamp(24px,4vw,54px))]"
        style={{ gridTemplateColumns: "minmax(0,1fr) auto", gridTemplateRows: "auto 1fr auto" }}
      >
        {/* Row 1: T logo + Destination (left)  |  Platform label + number (right) */}
        <div className="flex items-center gap-[clamp(6px,1.2vw,18px)] min-w-0">
          <div
            className="flex items-center justify-center rounded-full font-bold shrink-0"
            style={{
              width: "clamp(28px,5vw,74px)",
              height: "clamp(28px,5vw,74px)",
              backgroundColor: "#F58220",
              color: "#FFFFFF",
              fontSize: "clamp(15px,3vw,42px)",
              boxShadow: "0 0 0 2px #FFFFFF inset",
            }}
            data-testid="platform-t-logo"
          >
            T
          </div>
          <div
            className="font-bold truncate leading-none"
            style={{
              fontSize: "clamp(24px,5.4vw,80px)",
              color: "#1B2C4E",
            }}
            data-testid="platform-destination"
          >
            {destination || "Destination"}
          </div>
        </div>

        <div className="flex flex-col items-end justify-start">
          <span
            className="uppercase font-medium"
            style={{ fontSize: "clamp(10px,1.4vw,20px)", color: "#1B2C4E" }}
          >
            Platform
          </span>
          <span
            className="font-bold leading-none"
            style={{
              fontSize: "clamp(30px,6vw,88px)",
              color: "#F58220",
              marginTop: "clamp(-2px,-0.4vw,-6px)",
            }}
            data-testid="platform-number"
          >
            {platform || "–"}
          </span>
          {carriages && carriages.trim() && (
            <span
              className="mt-[clamp(4px,0.8vw,10px)] px-2 py-1 font-semibold"
              style={{
                fontSize: "clamp(9px,1.2vw,16px)",
                backgroundColor: "#1B2C4E",
                color: "#FFFFFF",
                letterSpacing: "0.02em",
              }}
              data-testid="platform-carriages"
            >
              {carriages}
            </span>
          )}
        </div>

        {/* Row 2: Divider + scrolling stops list  |  right column empty for balance */}
        <div
          className="relative min-w-0 col-span-2 overflow-hidden"
          style={{ minHeight: 0 }}
        >
          <div
            className="w-full"
            style={{ borderTop: "2px solid #1B2C4E", opacity: 0.5 }}
          />
          <div
            ref={viewportRef}
            className="relative overflow-hidden mt-[clamp(4px,0.8vw,12px)]"
            style={{ height: "calc(100% - clamp(6px,1vw,16px))" }}
            data-testid="platform-stops-viewport"
          >
            {trimmedStops.length > 0 ? (
              <div
                ref={listRef}
                className={shouldScroll ? "vscroll" : ""}
                style={{ "--vscroll-duration": `${duration}s` }}
                data-testid="platform-stops-list"
              >
                {doubled.map((stop, i) => (
                  <div
                    key={`${stop}-${i}`}
                    className="font-bold leading-tight"
                    style={{
                      fontSize: "clamp(18px,4vw,58px)",
                      color: "#1B2C4E",
                      padding: "clamp(1px,0.3vw,4px) 0",
                    }}
                  >
                    {stop}
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="font-semibold italic"
                style={{ fontSize: "clamp(14px,2vw,26px)", color: "#8090AA" }}
              >
                Add stopping stations in the control panel →
              </div>
            )}
          </div>
        </div>

        {/* Row 3: bottom bar — All Stops pill (left) + Departs (right) + Next Service */}
        <div className="col-span-2">
          <div className="flex items-end justify-between gap-2 pb-[clamp(4px,0.8vw,10px)]">
            {stopsLabel && stopsLabel.trim() ? (
              <span
                className="px-2.5 py-1 font-semibold"
                style={{
                  fontSize: "clamp(10px,1.3vw,18px)",
                  backgroundColor: "#1B2C4E",
                  color: "#FFFFFF",
                }}
                data-testid="platform-stops-label"
              >
                {stopsLabel}
              </span>
            ) : <span />}

            {(departsRelative && departsRelative.trim()) && (
              <div className="flex flex-col items-end leading-none">
                <span
                  className="uppercase font-medium"
                  style={{ fontSize: "clamp(10px,1.4vw,20px)", color: "#1B2C4E" }}
                >
                  Departs
                </span>
                <span
                  className="font-bold"
                  style={{
                    fontSize: "clamp(26px,5.4vw,72px)",
                    color: "#F58220",
                    marginTop: "clamp(-2px,-0.4vw,-6px)",
                  }}
                  data-testid="platform-departs-relative"
                >
                  {departsRelative}
                </span>
              </div>
            )}
          </div>

          {/* Next Service block */}
          {(nextDestination || nextPlatform || nextDeparts) && (
            <div
              className="border-t pt-[clamp(4px,0.8vw,10px)]"
              style={{ borderColor: "#1B2C4E33" }}
              data-testid="platform-next-service"
            >
              <div
                className="grid gap-x-3"
                style={{ gridTemplateColumns: "1fr auto auto" }}
              >
                <div
                  className="uppercase font-medium"
                  style={{ fontSize: "clamp(9px,1.15vw,15px)", color: "#5A6B85" }}
                >
                  Next Service
                </div>
                <div
                  className="uppercase font-medium text-right"
                  style={{ fontSize: "clamp(9px,1.15vw,15px)", color: "#5A6B85" }}
                >
                  Platform
                </div>
                <div
                  className="uppercase font-medium text-right"
                  style={{ fontSize: "clamp(9px,1.15vw,15px)", color: "#5A6B85" }}
                >
                  Departs
                </div>
                <div
                  className="font-semibold truncate"
                  style={{ fontSize: "clamp(12px,1.9vw,26px)", color: "#1B2C4E" }}
                  data-testid="platform-next-destination"
                >
                  {nextDestination || "—"}
                </div>
                <div
                  className="font-semibold text-right"
                  style={{ fontSize: "clamp(12px,1.9vw,26px)", color: "#1B2C4E" }}
                  data-testid="platform-next-platform"
                >
                  {nextPlatform || "—"}
                </div>
                <div
                  className="font-semibold text-right"
                  style={{ fontSize: "clamp(12px,1.9vw,26px)", color: "#1B2C4E" }}
                  data-testid="platform-next-departs"
                >
                  {nextDeparts || "—"}
                </div>
              </div>
              {nextVia && nextVia.trim() && (
                <div
                  className="italic"
                  style={{ fontSize: "clamp(9px,1.2vw,16px)", color: "#5A6B85" }}
                  data-testid="platform-next-via"
                >
                  {nextVia}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
